import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { VisualizationEvent, VisualizationFilters } from '../components/pitch/types';

/**
 * Hook for fetching match visualization data
 */
export const useMatchVisualizations = (
  matchId: string | undefined,
  filters: VisualizationFilters = {}
) => {
  const [events, setEvents] = useState<VisualizationEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchVisualizations = useCallback(async () => {
    if (!matchId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('match_visualization')
        .select('*')
        .eq('match_id', matchId)
        .eq('is_active', true)
        .order('event_timestamp', { ascending: true });

      // Apply filters
      if (filters.stat_type_id && filters.stat_type_id.length > 0) {
        query = query.in('stat_type_id', filters.stat_type_id);
      }

      if (filters.team_id && filters.team_id.length > 0) {
        if (filters.team_id.length === 1) {
          query = query.eq('team_id', filters.team_id[0]);
        } else {
          query = query.in('team_id', filters.team_id);
        }
      }

      if (filters.player_id) {
        query = query.eq('player_id', filters.player_id);
      }

      if (filters.start_time) {
        query = query.gte('event_timestamp', filters.start_time);
      }

      if (filters.end_time) {
        query = query.lte('event_timestamp', filters.end_time);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        console.error('Error fetching match visualizations:', fetchError);
        setError('Failed to fetch visualization data');
        return;
      }

      // Parse position_data JSON strings if they exist and normalize format
      let parsedEvents: VisualizationEvent[] = (data || []).map((event: any) => {
        let positionData = null;
        if (event.position_data) {
          try {
            // Handle both string and already parsed JSON
            positionData =
              typeof event.position_data === 'string'
                ? JSON.parse(event.position_data)
                : event.position_data;
            
            // Normalize: convert single object {x, y} to array [{x, y}]
            if (positionData && typeof positionData === 'object' && !Array.isArray(positionData)) {
              // Single object format: {x, y} -> convert to [{x, y}]
              if (typeof positionData.x === 'number' && typeof positionData.y === 'number') {
                positionData = [positionData];
              } else {
                positionData = null;
              }
            }
          } catch (e) {
            console.warn('Failed to parse position_data for event:', event.id, e);
          }
        }

        let tags = [];
        if (event.tags) {
          try {
            tags = typeof event.tags === 'string' ? JSON.parse(event.tags) : event.tags;
          } catch (e) {
            console.warn('Failed to parse tags for event:', event.id, e);
          }
        }

        return {
          ...event,
          position_data: positionData,
          tags: tags,
        };
      });

      // Client-side filtering for tags (since Supabase JSON array filtering is complex)
      if (filters.tags && filters.tags.length > 0) {
        parsedEvents = parsedEvents.filter((event) => {
          if (!event.tags || event.tags.length === 0) return false;
          // Check if event has at least one of the selected tags
          return filters.tags!.some((tag) => event.tags!.includes(tag));
        });
      }

      // Client-side filtering for match minutes
      if (filters.start_minute !== undefined || filters.end_minute !== undefined) {
        parsedEvents = parsedEvents.filter((event) => {
          if (event.match_minute === null) return false;
          const minute = event.match_minute;
          if (filters.start_minute !== undefined && minute < filters.start_minute) return false;
          if (filters.end_minute !== undefined && minute > filters.end_minute) return false;
          return true;
        });
      }

      setEvents(parsedEvents);
    } catch (err) {
      console.error('Error in useMatchVisualizations:', err);
      setError('Error fetching visualization data');
    } finally {
      setLoading(false);
    }
  }, [matchId, filters.stat_type_id, filters.team_id, filters.player_id, filters.tags, filters.start_time, filters.end_time, filters.start_minute, filters.end_minute]);

  useEffect(() => {
    fetchVisualizations();
  }, [fetchVisualizations]);

  return {
    events,
    loading,
    error,
    refetch: fetchVisualizations,
  };
};

